import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { useStore } from "../../../models";
import { IntentDto, SuggestionTestResult } from "@closerplatform/spinner-openapi";
import { FieldState } from "formstate";
import { validateRequired } from "@fidget/common/utils/validatorsFormState";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { SubtitleDescription } from "../../settings/subtitleDescription";
import {
  LanguageOption,
  LanguagePicker
} from "@fidget/common/components/form/languagePicker/languagePicker";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { AgentInput } from "@fidget/common/components/form/agentInput";
import { FullWidthBase } from "../../settings/settingsForms/base/fullWidthBase";
import { VerticalSeparator } from "../../settings/settingsForms/separators/verticalSeparator/verticalSeparator";
import {
  Level,
  LevelIndicator
} from "@fidget/common/components/indicators/levelIndicator/levelTextIndicator";
import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import agentInputStyles from "@fidget/common/components/form/agentInput.css";
import modelStyles from "../modelConfiguration/modelConfiguration.css";
import styles from "./testingTab.css";
import { logger } from "@fidget/common/utils";
import { SafeLocalStorageService } from "@fidget/common/services/safeLocalStorageService";
import { IntentConfiguration } from "../modelConfiguration/intentConfiguration";
import { staticConfig } from "@fidget/common/config/static-config";
import { useAgentServices } from "../../../models/agentServices";
import { AutoComplete } from "@fidget/common/components/form/autoComplete/autoComplete";
import { ITagGroup } from "src/models/tagGroups/TagGroupStore";
import { ITag } from "src/models/tags/TagsStore";

interface Props {
  resetScrollOffset: () => void;
}

export const AiTestingTab = (props: Props) => {
  const {
    currentLanguage,
    setCurrentLanguage,
    setTags,
    onTestClicked,
    inputFieldState,
    mostRecentTest,
    testsHistory,
    probabilityToLevel,
    currentlyViewedIntent,
    setCurrentlyViewedIntent,
    onIntentNameClicked
  } = aiTestingTabState(props);

  const { tagGroupsStore, tagsStore } = useStore();
  const [tagGroups, setTagGroups] = useState<Array<ITagGroup>>([]);
  useEffect(() => {
    tagGroupsStore.loadTagGroups().then(() => setTagGroups(tagGroupsStore.groups));
  }, []);

  const renderTitle = () => (
    <div className={commonStyles.title}>
      <L10n id={"ai.navigation.testing"} />
    </div>
  );

  const renderLanguagePicker = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.languagePicker.title")}
        description={intl.formatMessage("ai.testing.language.description")}
      />
      <div className={commonStyles.content}>
        <LanguagePicker
          onChange={(val) => (val ? setCurrentLanguage(val) : undefined)}
          currentLanguage={currentLanguage}
        />
      </div>
    </div>
  );

  const renderTestingMessageSection = () => {
    return (
      <>
        <div className={commonStyles.section}>
          <SubtitleDescription
            description={intl.formatMessage("ai.testing.tagGroup.description")}
          />
          <div className={commonStyles.content} style={{ width: "320px" }}>
            <AutoComplete<ITagGroup>
              onChange={(val) => setTags(val ? tagsStore.getFromIds(val.tagIds) : [])}
              onClearSelected={() => setTags([])}
              itemsProvider={() => tagGroups}
              itemToString={(group) => group?.name || ""}
              readOnly
              enableReset
            />
          </div>
        </div>
        <div className={commonStyles.section}>
          <SubtitleDescription description={intl.formatMessage("ai.testing.description")} />
          <div className={commonStyles.content}>
            <AgentInput
              className={agentInputStyles["limited-width"]}
              placeholder={intl.formatMessage("ai.testing.message.placeholder")}
              field={inputFieldState}
            />
          </div>
          <div className={styles["test-button"]}>
            <AgentButton onClick={onTestClicked}>
              {intl.formatMessage("ai.testing.button")}
            </AgentButton>
          </div>
        </div>
      </>
    );
  };

  const renderTestResult = (
    result: SuggestionTestResult,
    text: string,
    context: Array<string> | undefined,
    key: React.Key
  ) => (
    <div className={styles.results} key={key}>
      <FullWidthBase>
        <div className={styles["test-summary"]}>
          <div className={modelStyles["form-div"]}>
            <div
              className={classNames(modelStyles["description-font"], modelStyles["stat-header"])}
            >
              <L10n id="ai.testing.summary.level" />
            </div>
            <div>
              <LevelIndicator level={probabilityToLevel(result.probability)} />
            </div>
          </div>
          <VerticalSeparator />
          <div className={classNames(modelStyles["form-div"], modelStyles["intent-name"])}>
            <div
              className={classNames(modelStyles["description-font"], modelStyles["stat-header"])}
            >
              <L10n id="ai.testing.summary.intent" />
            </div>
            <p
              className={modelStyles["clickable-text"]}
              onClick={() => onIntentNameClicked(result.intentDto)}
            >
              {result.intentDto.intent}
            </p>
          </div>
          <VerticalSeparator />
          <div className={modelStyles["form-div"]}>
            <div
              className={classNames(modelStyles["description-font"], modelStyles["stat-header"])}
            >
              <L10n id="ai.testing.summary.message" />
            </div>
            <div className={modelStyles["stat-content"]}>{text}</div>
          </div>
          <VerticalSeparator />
          <div className={modelStyles["form-div"]}>
            <div
              className={classNames(modelStyles["description-font"], modelStyles["stat-header"])}
            >
              <L10n id="ai.testing.summary.tags" />
            </div>
            <div className={modelStyles["stat-content"]}>{context ? context.join(", ") : ""}</div>
          </div>
        </div>
      </FullWidthBase>
    </div>
  );

  const renderMostRecentTestSummary = () => {
    if (mostRecentTest && mostRecentTest.text && mostRecentTest.result) {
      return renderTestResult(
        mostRecentTest.result as SuggestionTestResult,
        mostRecentTest.text,
        mostRecentTest.context,
        0
      );
    }
    if (mostRecentTest && mostRecentTest.text && !mostRecentTest.result) {
      return <CloserLoaderComponent width="64px" height="64px" />;
    }

    return;
  };

  const renderTestsHistory = () =>
    testsHistory.map((test, idx) =>
      renderTestResult(test.result as SuggestionTestResult, test.text as string, test.context, idx)
    );

  const renderTestingTab = () => (
    <>
      {renderTitle()}
      {renderLanguagePicker()}
      {renderTestingMessageSection()}
      {renderMostRecentTestSummary()}
      {renderTestsHistory()}
    </>
  );

  const renderIntentView = (intent: IntentDto) => (
    <IntentConfiguration
      intentDto={intent}
      onGoBackClicked={() => setCurrentlyViewedIntent(undefined)}
    />
  );

  return currentlyViewedIntent ? renderIntentView(currentlyViewedIntent) : renderTestingTab();
};

interface TestData {
  text?: string;
  context?: Array<string>;
  result?: SuggestionTestResult;
}
type TestsHistory = ReadonlyArray<TestData>;

interface State {
  currentLanguage: LanguageOption;
  setCurrentLanguage: (value: LanguageOption) => void;
  tags: Array<ITag>;
  setTags: (value: Array<ITag>) => void;
  onTestClicked: () => Promise<void>;
  inputFieldState: FieldState<string>;
  testsHistory: TestsHistory;
  mostRecentTest?: TestData;
  probabilityToLevel: (probability: number) => Level;
  currentlyViewedIntent?: IntentDto;
  setCurrentlyViewedIntent: (dto?: IntentDto) => void;
  onIntentNameClicked: (dto: IntentDto) => void;
}
const aiTestingTabState = (props: Props): State => {
  const { spinnerService } = useAgentServices();
  const storage = SafeLocalStorageService.getInstance();
  const { currentUserStore, uiStore } = useStore();
  const [currentLanguage, setCurrentLanguage] = useState<LanguageOption>(
    currentUserStore.currentLanguage
  );
  const [tags, setTags] = useState<Array<ITag>>([]);
  const [inputFieldState] = useState<FieldState<string>>(createInputFieldState());
  const [testsHistory, setTestsHistory] = useState<TestsHistory>([]);
  const [mostRecentTest, setMostRecentTest] = useState<TestData>();
  const [currentlyViewedIntent, setCurrentlyViewedIntent] = useState<IntentDto>();
  const localStorageKey = `${currentUserStore.orgId}:aiModelTestsHistory`;

  useEffect(() => {
    const value = storage.getItem(localStorageKey);
    try {
      const savedTests: TestsHistory = value ? JSON.parse(value) : undefined;

      if (savedTests) {
        setTestsHistory(savedTests);
      }
    } catch (e) {
      logger.error({ message: "Coudln't load saved test history", error: e });
      storage.removeItem(localStorageKey);
    }
  }, []);

  const saveTest = (tests: TestsHistory) => {
    const trimmedTests = tests.slice(0, staticConfig.aiTestHistoryLimit);
    try {
      storage.setItem(localStorageKey, JSON.stringify(trimmedTests));
    } catch (e) {
      logger.error({ message: "Couldn't save tests history in storage", error: e });
    }
  };

  const onTestClicked = async () => {
    const validation = await inputFieldState.validate();
    if (!validation.hasError) {
      const history = mostRecentTest ? [mostRecentTest, ...testsHistory] : testsHistory;
      setTestsHistory(history);
      if (mostRecentTest) {
        setMostRecentTest(undefined);
      }

      const text = inputFieldState.$;
      const context = tags.map((tag) => tag.name);
      try {
        setMostRecentTest({ text, context });
        if (text) {
          inputFieldState.reset();
          const result = await spinnerService.testSuggestion({
            locale: currentLanguage,
            text,
            context
          });
          const test = { text, context, result };
          setMostRecentTest(test);
          saveTest([test, ...history]);
        }
      } catch (e) {
        logger.error({ message: "Couldn't test model", error: e });
        uiStore.addGenericErrorToast();
        setMostRecentTest(undefined);
      }
    }
  };

  const onIntentNameClicked = (intent: IntentDto) => {
    props.resetScrollOffset();
    setCurrentlyViewedIntent(intent);
  };

  const probabilityToLevel = (probability: number): Level => {
    if (probability >= staticConfig.aiProbabilityHighLevel) {
      return Level.High;
    }
    if (probability >= staticConfig.aiProbabilityMidLevel) {
      return Level.Mid;
    }
    return Level.Low;
  };

  return {
    currentLanguage,
    setCurrentLanguage,
    tags,
    setTags,
    onTestClicked,
    inputFieldState,
    mostRecentTest,
    testsHistory,
    probabilityToLevel,
    currentlyViewedIntent,
    setCurrentlyViewedIntent,
    onIntentNameClicked
  };
};

const createInputFieldState = () =>
  new FieldState("").setAutoValidationDefault(false).validators(validateRequired);
